import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

/*
   Pythagorean Theorem Calculator with the classic "square on each side" diagram.
   Squares grow/shrink smoothly (CSS transitions) as inputs change.
*/
export default function PythagoreanCalc() {
  const [find, setFind] = useState('c');
  const [a, setA] = useState(3);
  const [b, setB] = useState(4);
  const [c, setC] = useState(5);
  const result = useMemo(() => {
    if (find === 'c') return Math.sqrt(a*a + b*b);
    if (find === 'a') return Math.sqrt(c*c - b*b);
    if (find === 'b') return Math.sqrt(c*c - a*a);
    return null;
  }, [find, a, b, c]);

  // Effective values used for the visualization
  const av = find === 'a' ? result : a;
  const bv = find === 'b' ? result : b;
  const cv = find === 'c' ? result : c;

  // Scale so the whole diagram (triangle + 3 squares) fits in a 480×420 area
  const maxSide = Math.max(av || 1, bv || 1, cv || 1);
  const scale = 220 / (2 * maxSide + 20);
  const s = v => (v || 0) * scale;

  // Triangle vertices centered
  const cx0 = 210, cy0 = 260;
  const Ap = [cx0, cy0];
  const Bp = [cx0 + s(bv), cy0];
  const Cp = [cx0, cy0 - s(av)];

  // Squares:
  //   a² on the vertical leg (to the LEFT of it)
  const aSq = { x: Ap[0] - s(av), y: cy0 - s(av), size: s(av) };
  //   b² on the horizontal leg (BELOW it)
  const bSq = { x: Ap[0], y: cy0, size: s(bv) };
  //   c² on the hypotenuse — rotated outward (angle = ang above horizontal, rotate 90° outward)
  const angC = Math.atan2(Cp[1] - Bp[1], Cp[0] - Bp[0]); // direction from B to C
  const outX = Math.cos(angC + Math.PI/2) * s(cv);
  const outY = Math.sin(angC + Math.PI/2) * s(cv);
  const cSq = [
    Bp, Cp,
    [Cp[0] + outX, Cp[1] + outY],
    [Bp[0] + outX, Bp[1] + outY],
  ];

  return (
    <CalcShell look="cnet" title="Pythagorean Theorem Calculator" subtitle="Solve a² + b² = c² for any missing side. Includes animated 'squares on the sides' visualization.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Solve for</label>
            <select value={find} onChange={e => setFind(e.target.value)} data-testid="py-find">
              <option value="c">Hypotenuse c</option><option value="a">Leg a</option><option value="b">Leg b</option>
            </select>
          </div>
          {find !== 'a' && <div className="field"><label>Leg a</label><input type="number" step="any" value={a} onChange={e => setA(+e.target.value || 0)} data-testid="py-a"/></div>}
          {find !== 'b' && <div className="field"><label>Leg b</label><input type="number" step="any" value={b} onChange={e => setB(+e.target.value || 0)} data-testid="py-b"/></div>}
          {find !== 'c' && <div className="field"><label>Hypotenuse c</label><input type="number" step="any" value={c} onChange={e => setC(+e.target.value || 0)} data-testid="py-c"/></div>}

          <div className="result-panel mt-3"><div className="result-title">Result — side {find} ({find === 'c' ? 'hypotenuse' : 'leg'})</div><div className="result-value" data-testid="py-result">{isFinite(result) ? result.toFixed(6) : 'Invalid'}</div></div>
          {isFinite(result) && (() => {
            const sides = [av, bv, cv].sort((a, b) => a - b);
            const [s1, s2, s3] = sides;
            const equilateral = Math.abs(s1 - s3) < 1e-4;
            const isosceles = !equilateral && (Math.abs(s1 - s2) < 1e-4 || Math.abs(s2 - s3) < 1e-4);
            const bySides = equilateral ? 'equilateral' : isosceles ? 'isosceles' : 'scalene';
            const isPythagorean = Math.abs(s1 * s1 + s2 * s2 - s3 * s3) < 1e-2;
            return (
              <div className="mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm" data-testid="py-classification">
                <b>This is a right {bySides} triangle</b>{isPythagorean && s1 === Math.round(s1) && s2 === Math.round(s2) && s3 === Math.round(s3) ? <span className="text-emerald-700"> · Pythagorean triple!</span> : null}
              </div>
            );
          })()}
          {isFinite(result) && <table className="data-table mt-4">
            <tbody>
              <tr><td>a² (area of red square)</td><td className="text-right">{(av*av).toFixed(4)}</td></tr>
              <tr><td>b² (area of green square)</td><td className="text-right">{(bv*bv).toFixed(4)}</td></tr>
              <tr><td>a² + b²</td><td className="text-right font-semibold">{(av*av + bv*bv).toFixed(4)}</td></tr>
              <tr><td>c² (area of blue square)</td><td className="text-right font-semibold text-blue-700">{(cv*cv).toFixed(4)}</td></tr>
            </tbody>
          </table>}
        </div>

        <div className="bg-slate-50 rounded-2xl p-4">
          <svg viewBox="0 0 480 420" className="w-full">
            {/* Squares — labeled and animated by CSS transitions on rect attributes via inline style */}
            <rect x={aSq.x} y={aSq.y} width={aSq.size} height={aSq.size}
              fill="#fecaca" stroke="#dc2626" strokeWidth="2" style={{ transition: 'all 300ms ease' }}/>
            <text x={aSq.x + aSq.size/2} y={aSq.y + aSq.size/2 + 5} fontSize="14" fontWeight="bold" fill="#991b1b" textAnchor="middle">a² = {(av*av).toFixed(1)}</text>

            <rect x={bSq.x} y={bSq.y} width={bSq.size} height={bSq.size}
              fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" style={{ transition: 'all 300ms ease' }}/>
            <text x={bSq.x + bSq.size/2} y={bSq.y + bSq.size/2 + 5} fontSize="14" fontWeight="bold" fill="#166534" textAnchor="middle">b² = {(bv*bv).toFixed(1)}</text>

            <polygon points={cSq.map(p => p.join(',')).join(' ')}
              fill="#bfdbfe" stroke="#2563eb" strokeWidth="2" style={{ transition: 'all 300ms ease' }}/>
            <text x={(cSq[0][0]+cSq[2][0])/2} y={(cSq[0][1]+cSq[2][1])/2 + 4} fontSize="14" fontWeight="bold" fill="#1e40af" textAnchor="middle">c² = {(cv*cv).toFixed(1)}</text>

            {/* Triangle */}
            <polygon points={`${Ap.join(',')} ${Bp.join(',')} ${Cp.join(',')}`} fill="#0f172a" fillOpacity="0.08" stroke="#0f172a" strokeWidth="2" style={{ transition: 'all 300ms ease' }}/>
            {/* Right-angle marker */}
            <rect x={Ap[0]} y={Ap[1] - 12} width="12" height="12" fill="none" stroke="#0f172a" strokeWidth="1.5"/>

            <text x={Ap[0] - 8} y={Ap[1] + 14} fontSize="13" fontWeight="bold">A</text>
            <text x={Bp[0] + 4} y={Bp[1] + 14} fontSize="13" fontWeight="bold">B</text>
            <text x={Cp[0] - 14} y={Cp[1]} fontSize="13" fontWeight="bold">C</text>
          </svg>
          <p className="text-center text-xs text-slate-500 mt-2">Squares scale smoothly as you change any side.</p>
        </div>
      </div>

      <StepsPanel steps={
        find === 'c' ? [
          `<b>Step 1</b> — Recognize a right triangle: legs are <span class="font-mono">a</span> and <span class="font-mono">b</span>; hypotenuse is <span class="font-mono">c</span>.`,
          `<b>Step 2</b> — Apply Pythagorean theorem: <span class="font-mono">a² + b² = c²</span>.`,
          `<b>Step 3</b> — Substitute known legs: <span class="font-mono">(${a})² + (${b})² = c²</span> → <span class="font-mono">${(a * a).toFixed(3)} + ${(b * b).toFixed(3)} = c²</span> → <span class="font-mono">c² = ${(a * a + b * b).toFixed(3)}</span>.`,
          `<b>Step 4</b> — Take the positive square root: <span class="font-mono">c = √${(a * a + b * b).toFixed(3)} = <b>${result.toFixed(4)}</b></span>.`,
        ] : find === 'a' ? [
          `<b>Step 1</b> — Given the hypotenuse and one leg, solve <span class="font-mono">a² = c² − b²</span>.`,
          `<b>Step 2</b> — Substitute: <span class="font-mono">a² = (${c})² − (${b})² = ${(c * c).toFixed(3)} − ${(b * b).toFixed(3)} = ${(c * c - b * b).toFixed(3)}</span>.`,
          `<b>Step 3</b> — Take the positive square root: <span class="font-mono">a = √${(c * c - b * b).toFixed(3)} = <b>${result.toFixed(4)}</b></span>.`,
        ] : [
          `<b>Step 1</b> — Given the hypotenuse and one leg, solve <span class="font-mono">b² = c² − a²</span>.`,
          `<b>Step 2</b> — Substitute: <span class="font-mono">b² = (${c})² − (${a})² = ${(c * c).toFixed(3)} − ${(a * a).toFixed(3)} = ${(c * c - a * a).toFixed(3)}</span>.`,
          `<b>Step 3</b> — Take the positive square root: <span class="font-mono">b = √${(c * c - a * a).toFixed(3)} = <b>${result.toFixed(4)}</b></span>.`,
        ]
      } />
    </CalcShell>
  );
}
